<template>
  <div class="m19-cm">
    <div class="m19-cm__head text-body2 text-grey-8 q-mb-md">
      Документ М19 №<strong>{{ documentId }}</strong>
    </div>

    <q-banner v-if="!lines.length" rounded class="bg-grey-2 text-grey-7">
      Нет строк материалов для этого документа.
    </q-banner>

    <q-markup-table v-else flat bordered dense class="m19-cm__table q-mb-lg">
      <thead>
        <tr>
          <th class="text-left">Материал</th>
          <th class="text-right">Списано</th>
          <th class="text-left">Комментарий к строке</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in lines" :key="idx">
          <td class="text-left">{{ row.material_name || '—' }}</td>
          <td class="text-right text-weight-medium">{{ formatQty(row.fact_qty) }}</td>
          <td class="text-left m19-cm__comments">{{ row.comments || '—' }}</td>
        </tr>
      </tbody>
    </q-markup-table>

    <div class="row items-center q-mb-sm">
      <div class="text-subtitle2">Изменение цены сметы</div>
      <q-space />
      <q-btn
        flat
        dense
        no-caps
        color="primary"
        icon="help_outline"
        label="Как посчитано"
        @click="formulaOpen = true"
      />
    </div>

    <q-dialog v-model="formulaOpen" seamless no-refocus>
      <q-card ref="formulaCard" class="m19-cm__formula" :style="formulaCardStyle">
        <q-card-section
          class="row items-center q-pb-sm m19-cm__formula-bar"
          @mousedown="onFormulaDragStart"
        >
          <q-icon name="drag_indicator" size="20px" class="q-mr-sm" />
          <div>
            <div class="text-h6">Как посчитаны столбцы</div>
            <div class="text-caption text-grey-7">Потяните за шапку, чтобы сдвинуть окно</div>
          </div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup @mousedown.stop />
        </q-card-section>
        <q-separator />
        <q-card-section class="m19-cm__formula-body text-body2">
          <div v-if="sampleRows.length" class="m19-cm__live">
            <div class="text-caption text-grey-7 q-mb-xs">Строка из этой таблицы</div>
            <div class="m19-cm__chips q-mb-sm">
              <q-btn
                v-for="sample in sampleRows"
                :key="sample.idx"
                dense
                no-caps
                unelevated
                class="m19-cm__chip"
                :color="exampleIndex === sample.idx ? 'primary' : 'grey-3'"
                :text-color="exampleIndex === sample.idx ? 'white' : 'grey-9'"
                :label="sampleLabel(sample.row)"
                @click="exampleIndex = sample.idx"
              />
            </div>
            <div v-if="selectedSteps.length" class="m19-cm__steps">
              <div v-for="(step, stepIdx) in selectedSteps" :key="stepIdx" class="m19-cm__formula-line">
                <div><strong>{{ step.title }}</strong> {{ step.text }}</div>
                <div v-for="(note, noteIdx) in step.notes" :key="noteIdx" class="m19-cm__note">
                  {{ note }}
                </div>
              </div>
            </div>
          </div>

          <q-expansion-item
            dense
            expand-separator
            icon="calculate"
            label="Учебный пример с круглыми числами"
            header-class="text-body2"
            class="q-mt-sm"
          >
          <div class="q-pa-sm">
          <p>
            Списано <strong>40</strong>. В смете три строки, вместе <strong>400</strong>.
            Цена 1С с НДС этой строки — <strong>160</strong>.
          </p>
          <q-markup-table flat bordered dense class="m19-cm__example q-mb-md">
            <thead>
              <tr>
                <th class="text-left">Этаж</th>
                <th class="text-right">Кол-во строки</th>
                <th class="text-right">Списано на строку</th>
                <th class="text-right">Цена была</th>
                <th class="text-right">Цена стала</th>
                <th class="text-right">Сумма была</th>
                <th class="text-right">Сумма стала</th>
                <th class="text-right">Изменение</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td class="text-right">100</td>
                <td class="text-right">10</td>
                <td class="text-right">100,00</td>
                <td class="text-right">106,00</td>
                <td class="text-right">10 000,00</td>
                <td class="text-right">10 600,00</td>
                <td class="text-right">+ 600,00</td>
              </tr>
              <tr>
                <td>2</td>
                <td class="text-right">100</td>
                <td class="text-right">10</td>
                <td class="text-right">100,00</td>
                <td class="text-right">106,00</td>
                <td class="text-right">10 000,00</td>
                <td class="text-right">10 600,00</td>
                <td class="text-right">+ 600,00</td>
              </tr>
              <tr>
                <td>3</td>
                <td class="text-right">200</td>
                <td class="text-right">20</td>
                <td class="text-right">80,00</td>
                <td class="text-right">88,00</td>
                <td class="text-right">16 000,00</td>
                <td class="text-right">17 600,00</td>
                <td class="text-right">+ 1 600,00</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="7" class="text-right">Итого</td>
                <td class="text-right">+ 2 800,00</td>
              </tr>
            </tfoot>
          </q-markup-table>

          <p class="m19-cm__formula-line">
            <strong>Этаж 1, списано на строку:</strong> 40 × 100 / 400 = 10.
            Этаж 2 считается так же и тоже получает 10.
            Этаж 3: 40 × 200 / 400 = 20.
          </p>
          <p class="m19-cm__formula-line">
            <strong>Этаж 1, цена стала:</strong> ((100 − 10) × 100 + 10 × 160) / 100 = 106.
            Этаж 2 с той же старой ценой получает те же 106.
          </p>
          <p class="m19-cm__formula-line">
            <strong>Этаж 3, цена стала:</strong> ((200 − 20) × 80 + 20 × 160) / 200 = 88.
            Старая цена другая, поэтому новая цена тоже другая.
          </p>
          <p class="m19-cm__formula-line">
            <strong>Сумма была</strong> у этажа 1: 100 × 100 = 10 000.
            <strong>Сумма стала:</strong> 100 × 106 = 10 600.
            <strong>Изменение:</strong> 10 600 − 10 000 = 600.
          </p>
          <p class="m19-cm__formula-line">
            Этаж 3: сумма была 200 × 80 = 16 000, стала 200 × 88 = 17 600, изменение 1 600.
          </p>
          <p class="q-mb-none">
            <strong>Итого</strong> 600 + 600 + 1 600 = 2 800.
            Это только списанные 40 по разнице цен, а не всё количество строк по цене 1С.
          </p>
          </div>
          </q-expansion-item>
        </q-card-section>
      </q-card>
    </q-dialog>

    <div v-if="loading" class="text-grey-7 q-pa-sm">Загрузка изменения цены…</div>
    <q-banner v-else-if="loadError" rounded class="bg-red-1 text-negative">
      Не удалось загрузить изменение цены.
    </q-banner>
    <q-banner v-else-if="!priceLines.length" rounded class="bg-grey-2 text-grey-7">
      Для этого документа изменение цены по строкам сметы не записывалось.
    </q-banner>
    <template v-else>
      <div class="text-body2 text-grey-8 q-mb-sm">
        {{ changes.author_name || '—' }}, {{ changes.changed_at || '—' }}.
        Сумма сметы по этим строкам {{ sumDeltaLabel }}
      </div>
      <q-markup-table flat bordered dense class="m19-cm__table">
        <thead>
          <tr>
            <th class="text-left">Материал</th>
            <th class="text-right">ID проекта</th>
            <th class="text-left">Блок</th>
            <th class="text-right">Подъезд</th>
            <th class="text-right">Этаж</th>
            <th class="text-left">Работа</th>
            <th class="text-right">Кол-во строки</th>
            <th class="text-right">Списано на строку</th>
            <th class="text-right">Цена была</th>
            <th class="text-right">Цена стала</th>
            <th class="text-right">Сумма была</th>
            <th class="text-right">Сумма стала</th>
            <th class="text-right">Изменение</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in priceLines" :key="idx">
            <td class="text-left">{{ row.material_name || '—' }}</td>
            <td class="text-right">{{ row.project_id ?? '—' }}</td>
            <td class="text-left">{{ row.block_name || '—' }}</td>
            <td class="text-right">{{ row.entrance ?? '—' }}</td>
            <td class="text-right">{{ row.level_num ?? '—' }}</td>
            <td class="text-left">{{ row.work_set_name || '—' }}</td>
            <td class="text-right">{{ formatQty(row.material_amount) }}</td>
            <td class="text-right">{{ formatQty(row.allocated_qty) }}</td>
            <td class="text-right">{{ formatMoney(row.price_before) }}</td>
            <td class="text-right text-weight-medium">{{ formatMoney(row.price_after) }}</td>
            <td class="text-right">{{ formatMoney(row.sum_before) }}</td>
            <td class="text-right text-weight-medium">{{ formatMoney(row.sum_after) }}</td>
            <td class="text-right text-weight-medium">{{ formatSigned(row.sum_delta) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="m19-cm__total">
            <td colspan="12" class="text-right">Итого</td>
            <td class="text-right">{{ formatSigned(tableDelta) }}</td>
          </tr>
        </tfoot>
      </q-markup-table>
    </template>

    <div class="row justify-end q-mt-lg">
      <q-btn
        outline
        no-caps
        color="negative"
        icon="delete"
        label="Удалить документ"
        @click="openDeleteConfirm"
      />
    </div>

    <q-dialog v-model="deleteOpen" persistent>
      <q-card class="m19-cm__delete">
        <q-card-section>
          <div class="text-h6">Удалить документ М19 №{{ documentId }}?</div>
          <div class="text-body2 text-grey-8 q-mt-sm">
            Количество в смете не изменится. Снимется цена этого списания, суммы командных ТМЦ в оперфакте пересчитаются по оставшимся списаниям. В УСП сами не отправим.
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div v-if="deleteLoading" class="text-grey-7">Считаю, какая цена сметы станет после удаления…</div>
          <q-banner v-if="deleteError && !deleteLoading" rounded class="bg-red-1 text-negative q-mb-sm">
            {{ deleteError }}
          </q-banner>
          <template v-if="deletePreview && !deleteLoading">
            <q-banner v-if="!deletePreview.can_delete" rounded class="bg-orange-1 text-grey-9">
              {{ deletePreview.reason || "Этот документ удалить нельзя." }}
            </q-banner>
            <template v-else>
              <div v-if="deleteLines.length" class="text-body2 q-mb-sm">
                «Цена сейчас» — какая цена уже стоит в смете.
                «Цена станет» — какая останется, если убрать только этот документ.
                Другие списания этого материала останутся.
                Суммы командных ТМЦ в оперфакте пересчитаются по новой цене. В УСП сами не отправим.
              </div>
              <div v-else class="text-body2">
                По этому документу доли цены не записаны. Цена сметы не изменится.
                Документ будет отменён. Суммы оперфакта пересчитаются по оставшимся списаниям.
              </div>
              <q-markup-table
                v-if="deleteLines.length"
                flat
                bordered
                dense
                class="m19-cm__table m19-cm__delete-table q-mt-sm"
              >
                <thead>
                  <tr>
                    <th class="text-left">Материал</th>
                    <th class="text-right">Проект</th>
                    <th class="text-left">Блок</th>
                    <th class="text-right">Этаж</th>
                    <th class="text-right">Цена сейчас</th>
                    <th class="text-right">Цена станет</th>
                    <th class="text-right">Сумма сейчас</th>
                    <th class="text-right">Сумма станет</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in deleteLines" :key="idx">
                    <td class="text-left">{{ row.material_name || "—" }}</td>
                    <td class="text-right">{{ row.project_id ?? "—" }}</td>
                    <td class="text-left">{{ row.block_name || "—" }}</td>
                    <td class="text-right">{{ row.level_num ?? "—" }}</td>
                    <td class="text-right">{{ formatMoney(row.price_now) }}</td>
                    <td class="text-right text-weight-medium">{{ formatMoney(row.price_after_delete) }}</td>
                    <td class="text-right">{{ formatMoney(row.sum_now) }}</td>
                    <td class="text-right text-weight-medium">{{ formatMoney(row.sum_after_delete) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="m19-cm__total">
                    <td colspan="6" class="text-right">Итого по этим строкам</td>
                    <td class="text-right">{{ formatMoney(deletePreview.sum_now) }}</td>
                    <td class="text-right">{{ formatMoney(deletePreview.sum_after) }}</td>
                  </tr>
                  <tr class="m19-cm__total">
                    <td colspan="7" class="text-right">Изменение суммы</td>
                    <td class="text-right">{{ formatSigned(deletePreview.sum_delta) }}</td>
                  </tr>
                </tfoot>
              </q-markup-table>
            </template>
          </template>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn flat no-caps label="Отмена" :disable="deleting" v-close-popup />
          <q-btn
            v-if="deletePreview && deletePreview.can_delete"
            unelevated
            no-caps
            color="negative"
            label="Удалить"
            :loading="deleting"
            @click="confirmDelete"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { deleteM19Over, getM19PriceChanges, previewM19OverDelete } from "@/views/ClosingDocuments/services";

export default {
  name: "M19CommentMaterialsModal",

  props: {
    documentId: {
      type: [Number, String],
      default: null,
    },
    materials: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      loadError: false,
      changes: null,
      formulaOpen: false,
      formulaPos: null,
      exampleIndex: 0,
      deleteOpen: false,
      deleteLoading: false,
      deleteError: "",
      deletePreview: null,
      deleting: false,
    };
  },

  computed: {
    lines() {
      const m = this.materials;
      if (!m || !Array.isArray(m)) return [];
      return m.filter((x) => x && (x.material_name != null || x.fact_qty != null || x.comments));
    },
    priceLines() {
      const rows = this.changes && this.changes.lines;
      return Array.isArray(rows) ? rows : [];
    },
    deleteLines() {
      const rows = this.deletePreview && this.deletePreview.lines;
      return Array.isArray(rows) ? rows : [];
    },
    tableDelta() {
      const total = this.priceLines.reduce((acc, row) => acc + (Number(row.sum_delta) || 0), 0);
      return Math.round(total * 100) / 100;
    },
    sampleRows() {
      const rows = this.priceLines;
      if (!rows.length) return [];
      const indexes = [0];
      let largestIdx = 0;
      rows.forEach((row, idx) => {
        if (Number(row.material_amount) > Number(rows[largestIdx].material_amount)) largestIdx = idx;
      });
      if (!indexes.includes(largestIdx)) indexes.push(largestIdx);
      const firstPrice = Number(rows[0].price_before);
      const otherPriceIdx = rows.findIndex((row) => Number(row.price_before) !== firstPrice);
      if (otherPriceIdx > 0 && !indexes.includes(otherPriceIdx)) indexes.push(otherPriceIdx);
      if (indexes.length < 3 && rows.length > 2) {
        const mid = Math.floor(rows.length / 2);
        if (!indexes.includes(mid)) indexes.push(mid);
      }
      return indexes.slice(0, 3).map((idx) => ({ idx, row: rows[idx] }));
    },
    selectedSteps() {
      const row = this.priceLines[this.exampleIndex];
      if (!row) return [];
      const amount = Number(row.material_amount);
      const share = Number(row.allocated_qty);
      const before = Number(row.price_before);
      const after = Number(row.price_after);
      const calc = Number(row.price_calc);
      const sameMaterial = this.priceLines.filter((item) => item.material_id === row.material_id);
      const totalAmount = sameMaterial.reduce((sum, item) => sum + Number(item.material_amount || 0), 0);
      const written = sameMaterial.reduce((sum, item) => sum + Number(item.allocated_qty || 0), 0);
      const place = this.sampleLabel(row);
      const net = Number(row.price_net);
      const nds = Number(row.nds_percent);
      const plan = Number(row.price_plan);
      const prevQty = Number(row.prev_qty) || 0;
      const prevPrice = Number(row.prev_price_1c);
      const hasPlan = Number.isFinite(plan);
      const hasPrev = hasPlan && prevQty > 0 && Number.isFinite(prevPrice);
      const calcNote = Number.isFinite(net) && Number.isFinite(nds)
        ? `${this.formatShare(calc)} = ${this.formatShare(net)} × (1 + ${this.formatShare(nds)} / 100). ${this.formatShare(net)} — себестоимость 1С за штуку в строке М19. ${this.formatShare(nds)} — НДС этой строки сметы.`
        : `${this.formatShare(calc)} — цена 1С с НДС этой строки. Она записана в доле и в таблицу отдельным столбцом не выводится.`;
      const unsold = hasPrev ? amount - prevQty - share : amount - share;
      const steps = [
        {
          title: `${place}. Списано на строку:`,
          text: `${this.formatShare(written)} × ${this.formatShare(amount)} / ${this.formatShare(totalAmount)} = ${this.formatShare(share)}.`,
          notes: [
            `${this.formatShare(written)} — сколько списали по этому материалу. Это сумма столбца «Списано на строку».`,
            `${this.formatShare(amount)} — «Кол-во строки» у ${place}.`,
            `${this.formatShare(totalAmount)} — сумма «Кол-во строки» по этому материалу. Это количество сметы, предыдущие списания из него не вычитаются.`,
            `${this.formatShare(share)} — доля, записанная для этой строки.`,
          ],
        },
      ];
      if (Number.isFinite(calc) && hasPlan) {
        const priceText = hasPrev
          ? `((${this.formatShare(amount)} − ${this.formatShare(prevQty)} − ${this.formatShare(share)}) × ${this.formatMoney(plan)} + ${this.formatShare(prevQty)} × ${this.formatShare(prevPrice)} + ${this.formatShare(share)} × ${this.formatShare(calc)}) / ${this.formatShare(amount)} = ${this.formatMoney(after)}.`
          : `((${this.formatShare(amount)} − ${this.formatShare(share)}) × ${this.formatMoney(plan)} + ${this.formatShare(share)} × ${this.formatShare(calc)}) / ${this.formatShare(amount)} = ${this.formatMoney(after)}.`;
        const priceNotes = [
          `${this.formatMoney(plan)} — исходная цена сметы. По ней остаётся только ещё не списанное количество, ${this.formatShare(unsold)}.`,
          calcNote,
          `${this.formatMoney(after)} — «Цена стала»: сумма строки разделили на количество и оставили копейки.`,
        ];
        if (hasPrev) {
          priceNotes.splice(
            1,
            0,
            `${this.formatShare(prevQty)} — уже списано на эту строку предыдущими М19, по цене 1С ${this.formatShare(prevPrice)}.`,
            `${this.formatMoney(before)} — «Цена была», средняя цена всей строки до этого документа.`,
          );
        }
        steps.push({ title: "Цена стала:", text: priceText, notes: priceNotes });
      }
      const sumBeforeText = hasPrev
        ? `(${this.formatShare(amount)} − ${this.formatShare(prevQty)}) × ${this.formatMoney(plan)} + ${this.formatShare(prevQty)} × ${this.formatShare(prevPrice)} = ${this.formatMoney(row.sum_before)}.`
        : hasPlan
          ? `${this.formatShare(amount)} × ${this.formatMoney(plan)} = ${this.formatMoney(row.sum_before)}.`
          : this.formatMoney(row.sum_before);
      const sumAfterText = hasPlan && Number.isFinite(calc)
        ? hasPrev
          ? `(${this.formatShare(amount)} − ${this.formatShare(prevQty)} − ${this.formatShare(share)}) × ${this.formatMoney(plan)} + ${this.formatShare(prevQty)} × ${this.formatShare(prevPrice)} + ${this.formatShare(share)} × ${this.formatShare(calc)} = ${this.formatMoney(row.sum_after)}.`
          : `(${this.formatShare(amount)} − ${this.formatShare(share)}) × ${this.formatMoney(plan)} + ${this.formatShare(share)} × ${this.formatShare(calc)} = ${this.formatMoney(row.sum_after)}.`
        : this.formatMoney(row.sum_after);
      steps.push(
        {
          title: "Сумма была:",
          text: sumBeforeText,
          notes: [
            hasPrev
              ? "Остаток по исходной цене сметы плюс уже списанное по его цене 1С. На экране копейки."
              : "Количество строки умножили на исходную цену сметы.",
          ],
        },
        {
          title: "Сумма стала:",
          text: sumAfterText,
          notes: [
            "Та же раскладка после этого списания. На экране оставили копейки.",
          ],
        },
        {
          title: "Изменение:",
          text: `${this.formatMoney(row.sum_after)} − ${this.formatMoney(row.sum_before)} = ${this.formatSigned(row.sum_delta)}.`,
          notes: [
            "«Сумма стала» минус «Сумма была» в этой строке таблицы.",
          ],
        },
      );
      return steps;
    },
    formulaCardStyle() {
      if (!this.formulaPos) return {};
      return {
        position: "fixed",
        left: `${this.formulaPos.x}px`,
        top: `${this.formulaPos.y}px`,
        margin: "0",
      };
    },
    sumDeltaLabel() {
      const n = this.priceLines.length ? this.tableDelta : Number(this.changes && this.changes.sum_delta);
      if (!Number.isFinite(n)) return "—";
      const formatted = this.formatMoney(Math.abs(n));
      if (n > 0) return `выросла на ${formatted} с НДС`;
      if (n < 0) return `уменьшилась на ${formatted} с НДС`;
      return "не изменилась";
    },
  },

  beforeUnmount() {
    this.onFormulaDragEnd();
  },

  watch: {
    formulaOpen(open) {
      if (!open) this.formulaPos = null;
      if (open) this.exampleIndex = this.sampleRows[0] ? this.sampleRows[0].idx : 0;
    },
    priceLines() {
      this.exampleIndex = 0;
    },
    documentId: {
      immediate: true,
      handler() {
        this.loadPriceChanges();
      },
    },
  },

  methods: {
    async openDeleteConfirm() {
      this.deleteOpen = true;
      this.deleteLoading = true;
      this.deleteError = "";
      this.deletePreview = null;
      try {
        this.deletePreview = await previewM19OverDelete(this.documentId);
        if (!this.deletePreview) this.deleteError = "Не удалось посчитать цену после удаления.";
      } catch (e) {
        this.deleteError = this.deleteErrorText(e);
      } finally {
        this.deleteLoading = false;
      }
    },
    async confirmDelete() {
      const ids = (this.deletePreview && this.deletePreview.material_overuse_ids) || [];
      if (!ids.length || this.deleting) return;
      this.deleting = true;
      try {
        for (const id of ids) {
          await deleteM19Over(id);
        }
        this.deleteOpen = false;
        this.$q.notify({
          type: "positive",
          position: "bottom-left",
          message: "Документ удалён. Цена сметы и суммы командных ТМЦ в оперфакте пересчитаны.",
        });
        this.$emit("deleted");
      } catch (e) {
        this.deleteError = this.deleteErrorText(e);
      } finally {
        this.deleting = false;
      }
    },
    deleteErrorText(e) {
      const data = e && e.response && e.response.data;
      if (typeof data === "string" && data.trim()) return data;
      if (data && (data.error || data.message)) return String(data.error || data.message);
      return "Не удалось удалить документ.";
    },
    async loadPriceChanges() {
      if (this.documentId == null || this.documentId === "") {
        this.changes = null;
        this.loadError = false;
        return;
      }
      this.loading = true;
      this.loadError = false;
      const data = await getM19PriceChanges(this.documentId);
      this.loading = false;
      if (!data) {
        this.changes = null;
        this.loadError = true;
        return;
      }
      this.changes = data;
    },
    sampleLabel(row) {
      if (!row) return "Строка";
      const parts = [];
      if (row.level_num != null && row.level_num !== "") parts.push(`Этаж ${row.level_num}`);
      else if (row.block_name) parts.push(String(row.block_name));
      parts.push(this.formatShare(row.material_amount));
      return parts.join(" · ");
    },
    onFormulaDragStart(event) {
      if (event.button !== 0) return;
      const card = this.$refs.formulaCard?.$el || this.$refs.formulaCard;
      if (!card || typeof card.getBoundingClientRect !== "function") return;
      const rect = card.getBoundingClientRect();
      this.formulaPos = { x: rect.left, y: rect.top };
      this._formulaDrag = {
        dx: event.clientX - rect.left,
        dy: event.clientY - rect.top,
      };
      window.addEventListener("mousemove", this.onFormulaDragMove);
      window.addEventListener("mouseup", this.onFormulaDragEnd);
      event.preventDefault();
    },
    onFormulaDragMove(event) {
      if (!this._formulaDrag) return;
      const card = this.$refs.formulaCard?.$el || this.$refs.formulaCard;
      const width = card?.offsetWidth || 320;
      const height = card?.offsetHeight || 120;
      const x = Math.min(window.innerWidth - 48, Math.max(0, event.clientX - this._formulaDrag.dx));
      const y = Math.min(window.innerHeight - 48, Math.max(0, event.clientY - this._formulaDrag.dy));
      const maxX = Math.max(0, window.innerWidth - Math.min(width, window.innerWidth));
      const maxY = Math.max(0, window.innerHeight - Math.min(height, 80));
      this.formulaPos = {
        x: Math.min(x, maxX),
        y: Math.min(y, maxY),
      };
    },
    onFormulaDragEnd() {
      this._formulaDrag = null;
      window.removeEventListener("mousemove", this.onFormulaDragMove);
      window.removeEventListener("mouseup", this.onFormulaDragEnd);
    },
    formatQty(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n.toLocaleString("ru-RU", { maximumFractionDigits: 4 });
    },
    formatShare(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n.toLocaleString("ru-RU", { maximumFractionDigits: 8 });
    },
    formatSigned(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      const formatted = this.formatMoney(Math.abs(n));
      if (n > 0) return `+ ${formatted}`;
      if (n < 0) return `− ${formatted}`;
      return this.formatMoney(0);
    },
    formatMoney(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style lang="scss" scoped>
.m19-cm {
  width: 100%;
  min-width: 0;
  max-width: none;

  &__delete {
    width: min(980px, 96vw);
  }

  &__delete-table {
    max-height: 360px;
    overflow: auto;

    :deep(td:first-child),
    :deep(th:first-child) {
      white-space: normal;
      min-width: 12rem;
    }
  }

  &__head {
    font-size: 14px;
  }

  &__table {
    font-size: 13px;
    width: 100%;

    :deep(th),
    :deep(td) {
      padding: 8px 10px;
      white-space: nowrap;
    }

    :deep(th) {
      font-weight: 600;
      font-size: 12px;
    }

    :deep(tfoot td) {
      font-weight: 700;
      background: #f8fafc;
      border-top: 2px solid #cbd5e1;
    }
  }

  &__formula {
    width: min(560px, 94vw);
    height: 520px;
    min-width: 420px;
    min-height: 280px;
    max-width: 96vw;
    max-height: 90vh;
    resize: both;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }

  &__formula-bar {
    cursor: move;
    user-select: none;
    flex-shrink: 0;
  }

  &__formula-body {
    overflow: auto;
  }

  &__formula-line {
    margin-bottom: 8px;
  }

  &__live {
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafc;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__chip {
    border-radius: 8px;
  }

  &__steps {
    margin-top: 4px;
  }

  &__note {
    margin-top: 2px;
    color: #64748b;
    font-size: 12px;
    line-height: 1.35;
  }

  &__example {
    font-size: 13px;

    :deep(th),
    :deep(td) {
      padding: 6px 8px;
      white-space: nowrap;
    }

    :deep(tfoot td) {
      font-weight: 700;
      background: #f8fafc;
    }
  }

  &__comments {
    white-space: pre-wrap;
    word-break: break-word;
    min-width: 220px;
    max-width: none;
  }
}
</style>
